import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Grid,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { usePopularPodcastsByLanguage, useAvailableLanguages, useLocalPodcastEpisodes } from '../../hooks/useMetrics';
import { usePodcastEpisodesInfinite, useAddEpisodeToProject } from '../../hooks/usePodcasts';
import { useProjects } from '../../hooks/useProjects';
import { FirstEpisodeTutorial } from '../../components/onboarding/FirstEpisodeTutorial';
import type { PodcastEpisode, PopularPodcastDetail, PodcastEpisodeWithProjectCount } from '../../types/api.types';
import { useMemo } from 'react';

export const PopularPodcasts = () => {
  const { t } = useTranslation('podcasts');
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedPodcast, setSelectedPodcast] = useState<PopularPodcastDetail | null>(null);
  const [showAllEpisodes, setShowAllEpisodes] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState<{
    podcast: PopularPodcastDetail;
    episode: PodcastEpisode;
  } | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [pendingPlayerUrl, setPendingPlayerUrl] = useState<string | null>(null);
  const [showFirstEpisodePopup, setShowFirstEpisodePopup] = useState(false);
  const [lastAddedEpisodeUuid, setLastAddedEpisodeUuid] = useState<string | null>(null);

  const { data: availableLanguages, isLoading: isLoadingLanguages } = useAvailableLanguages();
  const { data: popularPodcasts, isLoading: isLoadingPopular, error: popularError } = usePopularPodcastsByLanguage(selectedLanguage, !!selectedLanguage);
  const { data: localEpisodes, isLoading: isLoadingLocal } = useLocalPodcastEpisodes(selectedPodcast?.uuid || '', !!selectedPodcast && !showAllEpisodes);
  const { 
    data: episodesData, 
    isLoading: isLoadingEpisodes, 
    error: episodesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePodcastEpisodesInfinite(selectedPodcast?.name || '', !!selectedPodcast && showAllEpisodes);
  const { projects } = useProjects();
  const { mutate: addEpisode, isPending: isAdding } = useAddEpisodeToProject();

  // Calculate total episodes across all projects for tutorial
  const totalEpisodeCount = useMemo(() => {
    if (!projects) return 0;
    return projects.reduce((sum, project) => sum + (project.podcastEpisodes?.length || 0), 0);
  }, [projects]);

  // Flatten episodes from infinite query
  const podcast = useMemo(() => {
    if (!episodesData?.pages) return null;
    const firstPage = episodesData.pages[0];
    const allEpisodes = episodesData.pages.flatMap(page => page.podcastEpisodes || []);
    return {
      ...firstPage,
      podcastEpisodes: allEpisodes,
    };
  }, [episodesData]);

  const loadedEpisodesCount = podcast?.podcastEpisodes?.length || 0;
  const totalEpisodesCount = episodesData?.pages?.[0]?.totalEpisodesCount || 0;

  // Ref for intersection observer (episodes infinite scroll)
  const episodesObserverTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll for episodes
  useEffect(() => {
    if (showAllEpisodes && selectedPodcast) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );

      const currentTarget = episodesObserverTarget.current;
      if (currentTarget) {
        observer.observe(currentTarget);
      }

      return () => {
        if (currentTarget) {
          observer.unobserve(currentTarget);
        }
      };
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, showAllEpisodes, selectedPodcast]);

  // Set initial language when available languages load
  if (availableLanguages && availableLanguages.length > 0 && !selectedLanguage) {
    setSelectedLanguage(availableLanguages[0]);
  }

  const handleLanguageChange = (event: any) => {
    setSelectedLanguage(event.target.value);
    setSelectedPodcast(null); // Reset selection when language changes
    setShowAllEpisodes(false);
  };

  const handleSelectPodcast = (popularPodcast: PopularPodcastDetail) => {
    setSelectedPodcast(popularPodcast);
    setShowAllEpisodes(false);
  };

  const handleBackToResults = () => {
    setSelectedPodcast(null);
    setShowAllEpisodes(false);
  };

  const handleAddEpisode = (episode: PodcastEpisode | PodcastEpisodeWithProjectCount) => {
    if (!selectedPodcast) return;
    setSelectedEpisode({ podcast: selectedPodcast, episode });
  };

  const handleConfirmAdd = () => {
    if (!selectedEpisode || !selectedProjectId) return;

    const { episode, podcast: podcastDetails } = selectedEpisode;

    // Use the podcast metadata captured when the user clicked "Add".
    // `podcast` (from the infinite-scroll query) is only populated when
    // showAllEpisodes=true, so we cannot rely on it here — the local-episodes
    // view leaves it null and the button would silently do nothing.
    // PopularPodcastDetail doesn't carry rssUrl/websiteUrl; empty strings are
    // safe because the podcast already exists in the DB whenever local episodes
    // are shown, so the backend looks it up by UUID and ignores those fields.
    const podcastPayload = podcast
      ? {
          uuid: podcast.uuid,
          name: podcast.name,
          language: podcast.language,
          rssUrl: podcast.rssUrl,
          imageUrl: podcast.imageUrl,
          websiteUrl: podcast.websiteUrl,
          description: podcast.description,
        }
      : {
          uuid: podcastDetails.uuid,
          name: podcastDetails.name,
          language: podcastDetails.language,
          rssUrl: '',
          imageUrl: podcastDetails.imageUrl,
          websiteUrl: '',
          description: podcastDetails.description,
        };

    addEpisode(
      {
        projectId: selectedProjectId as number,
        payload: {
          podcast: podcastPayload,
          episode: {
            uuid: episode.uuid,
            datePublished: episode.datePublished,
            name: episode.name,
            description: episode.description,
            imageUrl: episode.imageUrl,
            subTitle: episode.subTitle,
            audioUrl: episode.audioUrl,
            videoUrl: episode.videoUrl,
            fileLength: episode.fileLength,
            fileType: episode.fileType,
            duration: episode.duration,
            isExplicit: episode.isExplicit,
          },
        },
      },
      {
      onSuccess: (response) => {
        console.log('Episode added successfully:', response);
        console.log('Response podcastId:', response?.podcastId);
        console.log('Selected projectId:', selectedProjectId);
        console.log('Total episodes before add:', totalEpisodeCount);
        
        // Close dialog
        setSelectedEpisode(null);
        setSelectedProjectId('');
        
        // If we got a podcast ID back and this is the first episode, show tutorial
        if (response?.podcastId && selectedProjectId && totalEpisodeCount === 0) {
          const playerUrl = `/player/${selectedProjectId}/podcast/${response.podcastId}`;
          console.log('First episode! Showing tutorial');
          setLastAddedEpisodeUuid(episode.uuid);
          setPendingPlayerUrl(playerUrl);
          setShowFirstEpisodePopup(true);
        } else {
          console.log('Episode added - dialog closed');
        }
      },
      }
    );
  };

const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const sanitizeHtml = (html: string) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  };

  const truncateHtml = (html: string, maxLength: number) => {
    const temp = document.createElement('div');
    temp.innerHTML = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    const text = temp.textContent || temp.innerText || '';

    if (text.length <= maxLength) {
      return sanitizeHtml(html);
    }

    const truncated = text.substring(0, maxLength);
    temp.textContent = truncated + '...';
    return DOMPurify.sanitize(temp.innerHTML, { ALLOWED_TAGS: [] });
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('popularPodcasts')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('browseByLanguage')}
      </Typography>

      {/* Language Selector */}
      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel>{t('selectLanguage')}</InputLabel>
        <Select
          value={selectedLanguage}
          label={t('selectLanguage')}
          onChange={handleLanguageChange}
          disabled={isLoadingLanguages || !availableLanguages || availableLanguages.length === 0}
        >
          {availableLanguages && availableLanguages.map((lang) => (
            <MenuItem key={lang} value={lang}>
              {lang}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Loading Languages State */}
      {isLoadingLanguages && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {t('loadingLanguages')}
          </Typography>
        </Box>
      )}

      {/* No Languages Available */}
      {!isLoadingLanguages && (!availableLanguages || availableLanguages.length === 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('noLanguagesAvailable')}
        </Alert>
      )}

      {/* Loading State */}
      {isLoadingPopular && selectedLanguage && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {popularError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('failedToLoad')}
        </Alert>
      )}

      {/* Popular Podcasts Results */}
      {popularPodcasts && !isLoadingPopular && !selectedPodcast && (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('popularPodcasts')} ({popularPodcasts.length})
          </Typography>
          
          {popularPodcasts.length === 0 ? (
            <Alert severity="info">
              {t('noPodcastsFound')}
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {popularPodcasts.map((result) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={result.uuid}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onClick={() => handleSelectPodcast(result)}
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={result.imageUrl || '/placeholder-podcast.png'}
                      alt={result.name}
                      sx={{ objectFit: 'contain', bgcolor: 'action.hover', p: 1 }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom noWrap>
                        {result.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                          label={result.language}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={t('episode', { count: result.episodeCount })}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtml(truncateHtml(result.description || '', 150))
                        }}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Episodes View - Show when podcast is selected */}
      {selectedPodcast && (
        <Box>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBackToResults}
            sx={{ mb: 2 }}
          >
            {t('backToResults')}
          </Button>

          {isLoadingEpisodes && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {episodesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('failedToLoadEpisodes')}
            </Alert>
          )}

          {/* Show local episodes first */}
          {!showAllEpisodes && localEpisodes && !isLoadingLocal && selectedPodcast && (
            <Grid container spacing={3}>
              {/* Podcast Info */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardMedia
                    component="img"
                    height="300"
                    image={selectedPodcast.imageUrl || '/placeholder-podcast.png'}
                    alt={selectedPodcast.name}
                    sx={{ objectFit: 'contain', bgcolor: 'action.hover', p: 1 }}
                  />
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {selectedPodcast.name}
                    </Typography>
                    <Chip
                      label={selectedPodcast.language}
                      size="small"
                      color="primary"
                      sx={{ mb: 2 }}
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedPodcast.description || '') }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Episodes List */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    {t('episodesInDatabase', { count: localEpisodes.length })}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowAllEpisodes(true)}
                    disabled={showAllEpisodes}
                  >
                    {t('getMoreEpisodes')}
                  </Button>
                </Box>
                
                {localEpisodes.length > 0 ? (
                  <List>
                    {localEpisodes.map((episode) => (
                      <Card
                        key={episode.uuid}
                        sx={{ mb: 2 }}
                        {...(episode.uuid === lastAddedEpisodeUuid
                          ? { 'data-tour': 'first-added-episode' }
                          : undefined)}
                      >
                        <ListItem
                          sx={{
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'flex-start',
                          }}
                        >
                          {episode.imageUrl && (
                            <CardMedia
                              component="img"
                              sx={{ width: 100, height: 100, mr: 2, borderRadius: 1 }}
                              image={episode.imageUrl}
                              alt={episode.name}
                            />
                          )}
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle1" component="span">
                                  {episode.name}
                                </Typography>
                                {episode.isExplicit && (
                                  <Chip label={t('explicit')} size="small" color="warning" />
                                )}
                                {episode.projectCount > 0 && (
                                  <Chip 
                                    label={t('user', { count: episode.projectCount })} 
                                    size="small" 
                                    color="success" 
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  paragraph
                                  dangerouslySetInnerHTML={{ 
                                    __html: sanitizeHtml(truncateHtml(episode.description || '', 200))
                                  }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(episode.datePublished)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('duration', { duration: formatDuration(episode.duration) })}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                          <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
                            <IconButton
                              color="primary"
                              onClick={() => handleAddEpisode(episode)}
                            >
                              <AddIcon />
                            </IconButton>
                            {episode.audioUrl && (
                              <IconButton
                                href={episode.audioUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PlayIcon />
                              </IconButton>
                            )}
                          </Box>
                        </ListItem>
                      </Card>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    {t('noEpisodesInDatabase')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {/* Show all episodes from Taddy when requested */}
          {showAllEpisodes && podcast && !isLoadingEpisodes && (
            <Grid container spacing={3}>
              {/* Podcast Info */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardMedia
                    component="img"
                    height="300"
                    image={podcast.imageUrl || '/placeholder-podcast.png'}
                    alt={podcast.name}
                    sx={{ objectFit: 'contain', bgcolor: 'action.hover', p: 1 }}
                  />
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {podcast.name}
                    </Typography>
                    <Chip
                      label={podcast.language}
                      size="small"
                      color="primary"
                      sx={{ mb: 2 }}
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(podcast.description || '') }}
                    />
                    {podcast.websiteUrl && (
                      <Button
                        size="small"
                        href={podcast.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('visitWebsite')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Episodes List from Taddy */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    {t('allEpisodes', { count: loadedEpisodesCount })} {totalEpisodesCount > 0 && `of ${totalEpisodesCount}`}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowAllEpisodes(false)}
                  >
                    {t('showPopular')}
                  </Button>
                </Box>
                
                {podcast.podcastEpisodes && podcast.podcastEpisodes.length > 0 ? (
                  <List>
                    {podcast.podcastEpisodes.map((episode) => (
                      <Card
                        key={episode.uuid}
                        sx={{ mb: 2 }}
                        {...(episode.uuid === lastAddedEpisodeUuid
                          ? { 'data-tour': 'first-added-episode' }
                          : undefined)}
                      >
                        <ListItem
                          sx={{
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'flex-start',
                          }}
                        >
                          {episode.imageUrl && (
                            <CardMedia
                              component="img"
                              sx={{ width: 100, height: 100, mr: 2, borderRadius: 1 }}
                              image={episode.imageUrl}
                              alt={episode.name}
                            />
                          )}
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle1" component="span">
                                  {episode.name}
                                </Typography>
                                {episode.isExplicit && (
                                  <Chip label={t('explicit')} size="small" color="warning" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  paragraph
                                  dangerouslySetInnerHTML={{ 
                                    __html: sanitizeHtml(truncateHtml(episode.description || '', 200))
                                  }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(episode.datePublished)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('duration', { duration: formatDuration(episode.duration) })}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                          <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
                            <IconButton
                              color="primary"
                              onClick={() => handleAddEpisode(episode)}
                            >
                              <AddIcon />
                            </IconButton>
                            {episode.audioUrl && (
                              <IconButton
                                href={episode.audioUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PlayIcon />
                              </IconButton>
                            )}
                          </Box>
                        </ListItem>
                      </Card>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    {t('noEpisodesFound')}
                  </Typography>
                )}

                {/* Intersection observer target for infinite scroll */}
                <Box ref={episodesObserverTarget} sx={{ py: 2, textAlign: 'center' }}>
                  {isFetchingNextPage && (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">
                        {t('loadingMoreEpisodes')}
                      </Typography>
                    </Box>
                  )}
                  {!hasNextPage && loadedEpisodesCount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t('allEpisodesLoaded')}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Add to Project Dialog */}
      <Dialog
        open={!!selectedEpisode}
        onClose={() => setSelectedEpisode(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('addEpisodeToProject')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('selectProjectPrompt', { episodeName: selectedEpisode?.episode.name || '' })}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('selectProject')}</InputLabel>
            <Select
              value={selectedProjectId}
              label={t('selectProject')}
              onChange={(e) => setSelectedProjectId(e.target.value as number)}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.projectName} ({project.projectLanguage})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEpisode(null)}>{t('cancel')}</Button>
          <Button
            onClick={handleConfirmAdd}
            variant="contained"
            disabled={!selectedProjectId || isAdding}
          >
            {isAdding ? <CircularProgress size={24} /> : t('addToProjectButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* First Episode Tutorial */}
      <FirstEpisodeTutorial
        open={showFirstEpisodePopup}
        onComplete={() => {
          setShowFirstEpisodePopup(false);
          if (pendingPlayerUrl) {
            navigate(pendingPlayerUrl);
            setPendingPlayerUrl(null);
          }
        }}
        onSkip={() => {
          setShowFirstEpisodePopup(false);
          setPendingPlayerUrl(null);
        }}
        playerUrl={pendingPlayerUrl}
      />
    </Box>
  );
};
